import React, { useState, useMemo, useEffect, useRef } from 'react';
import './DesktopPeopleBrowse.css';

function DesktopPeopleBrowse({ donors, groups, onSelectPerson, onAddPerson, highlightedPeople = [] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy] = useState('name'); // 'name' | 'amount' | 'recent'
  const [hoveredPerson, setHoveredPerson] = useState(null);
  const tileRefs = useRef({});

  // Scroll to first highlighted person when highlights change
  useEffect(() => {
    if (highlightedPeople.length > 0) {
      const firstHighlighted = highlightedPeople[0];
      const tileElement = tileRefs.current[firstHighlighted];
      if (tileElement) {
        // Small delay to let the highlight animation start
        setTimeout(() => {
          tileElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }, 100);
      }
    }
  }, [highlightedPeople]);

  // Convert donors to list and apply filtering/sorting
  const people = useMemo(() => {
    const donorList = Object.entries(donors || {}).map(([name, data]) => ({
      name,
      ...data
    }));

    // Filter by search
    let filtered = donorList;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = donorList.filter(d =>
        d.name.toLowerCase().includes(query) ||
        (d.narrative && d.narrative.toLowerCase().includes(query))
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      if (sortBy === 'amount') {
        const amtA = parseFloat(String(a.amount || '0').replace(/[^0-9.]/g, '')) || 0;
        const amtB = parseFloat(String(b.amount || '0').replace(/[^0-9.]/g, '')) || 0;
        return amtB - amtA;
      }
      if (sortBy === 'recent') {
        const dateA = new Date(a.lastContact || a.last_contact || 0);
        const dateB = new Date(b.lastContact || b.last_contact || 0);
        return dateB - dateA;
      }
      return a.name.localeCompare(b.name);
    });
  }, [donors, searchQuery, sortBy]);

  // Color palette matching group members
  const getPersonColor = (name) => {
    const colors = [
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#38bdf8', // sky blue
      '#a78bfa', // purple
      '#ec4899', // pink
      '#3b82f6', // blue
      '#14b8a6', // teal
      '#818cf8', // indigo
    ];
    const index = name ? name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length : 0;
    return colors[index];
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const getGroupName = (person) => {
    if (person.groupId || person.group_id) {
      const group = groups?.find(g => g.id === person.groupId || g.id === person.group_id);
      return group?.name || null;
    }
    return null;
  };

  // Format amount for display
  const formatAmount = (amount) => {
    if (!amount || amount === 'Unknown') return null;
    const num = parseFloat(String(amount).replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return null;
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num}`;
  };

  return (
    <div className="desktop-people-browse">
      {/* Header section - top left */}
      <div className="people-browse-header">
        <h1 className="people-browse-title">People</h1>

        <div className="people-header-actions">
          <div className="people-search-bar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {onAddPerson && (
            <button className="add-person-btn" onClick={onAddPerson}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Add Person
            </button>
          )}
        </div>
      </div>

      {/* Tile grid area */}
      <div className="people-tile-area">
        {people.length === 0 ? (
          <div className="people-empty-state">
            {searchQuery ? (
              <div className="people-empty-search">No matches found</div>
            ) : (
              <button className="people-empty-add" onClick={onAddPerson}>
                <div className="empty-tooltip">
                  <span>Add your first person</span>
                  <div className="empty-tooltip-arrow"></div>
                </div>
                <div className="empty-avatar">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <span className="empty-text">No one's here yet...</span>
              </button>
            )}
          </div>
        ) : (
          <div className="people-tile-grid">
            {people.map((person, idx) => {
              const color = getPersonColor(person.name);
              const amountDisplay = formatAmount(person.amount);
              const groupName = getGroupName(person);
              const isHighlighted = highlightedPeople.includes(person.name);

              // Calculate row and column for diagonal animation
              const cols = 10;
              const row = Math.floor(idx / cols);
              const col = idx % cols;
              const diagonalDelay = (row + col) * 0.03;

              return (
                <button
                  key={person.name}
                  ref={el => tileRefs.current[person.name] = el}
                  className={`person-tile ${hoveredPerson === person.name ? 'hovered' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                  onClick={() => onSelectPerson?.(person.name)}
                  onMouseEnter={() => setHoveredPerson(person.name)}
                  onMouseLeave={() => setHoveredPerson(null)}
                  style={{
                    animationDelay: `${diagonalDelay}s`,
                    borderColor: color,
                  }}
                >
                  <span
                    className="person-tile-initials"
                    style={{
                      color: color,
                    }}
                  >
                    {getInitials(person.name)}
                  </span>

                  {amountDisplay && (
                    <div
                      className="person-tile-badge"
                      style={{ background: color }}
                    >
                      {amountDisplay}
                    </div>
                  )}

                  {/* Hover tooltip */}
                  <div className="person-tile-tooltip">
                    <span className="tooltip-name">{person.name}</span>
                    {groupName && (
                      <span className="tooltip-group" style={{ color }}>{groupName}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DesktopPeopleBrowse;
